/**
 * ADR-0221 §3.2 — Issues (`/app/issues`): `ActionItemsList.tsx` copied and
 * trimmed to the issue's columns. The toolbar's lens (ADR-0173), *Show
 * closed*, *My issues*, *My teams* and the title search; the active-filter
 * chip strip; sortable headers with funnel filters; the URL is the state,
 * remembered per account (`lib/listViewMemory.ts`); cursor *Load more* on the
 * default sort; the *Refreshing…* overlay. Every status word and date is the
 * hub's; the console owns labels only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import type { MemberCandidate } from "../components/MemberPicker.js";
import { FilterChip, HeaderCell, MultiSelectPopover, TextFilterPopover } from "../components/listFilters.js";
import { applyViewState, readViewState, serializeViewState, viewStateInUrl, writeViewState } from "../lib/listViewMemory.js";
import { fmtDay, initiativeLabel } from "./TenantInitiatives.js";

// ── the served shapes (hub src/initiatives/issues/types.ts) ──────────────────

export type IssueStatus = "open" | "resolved" | "closed" | "dismissed";
export type IssueListItem = {
  id: string;
  title: string;
  status: IssueStatus;
  /** ADR-0220 Amendment 3: `ref` (I-006) shown beside the name. */
  initiative: { id: string; ref?: string; name: string };
  assignee: { name: string } | null;
  raised_by: { name: string } | null;
  team: { id: string; name: string } | null;
  resolution_label: string | null;
  created_at: string;
  status_changed_at: string | null;
  age_days: number;
  action_items: { open: number; total: number };
};

export const ISSUE_STATUSES: IssueStatus[] = ["open", "resolved", "closed", "dismissed"];
export const STATUS_WORDS: Record<IssueStatus, string> = { open: "Open", resolved: "Resolved", closed: "Closed", dismissed: "Dismissed" };
/** Spec §3.2: Open warn, Resolved ok, Closed plain, Dismissed neutral. */
export function statusPillClass(s: IssueStatus): string {
  return s === "open" ? "pill pillWarn" : s === "resolved" ? "pill pillOk" : s === "closed" ? "pill" : "pill pillNeutral";
}

/** Age (spec §3.2): days open while open; the date of the move once resolved, closed or dismissed. */
export function ageCell(i: IssueListItem): string {
  if (i.status === "open" || !i.status_changed_at) return `${i.age_days} ${i.age_days === 1 ? "day" : "days"}`;
  return fmtDay(i.status_changed_at);
}

export function actionItemsCell(i: IssueListItem): string {
  return i.action_items.total === 0 ? "—" : `${i.action_items.open} open / ${i.action_items.total}`;
}

const REMEMBERED_PARAMS = ["title_contains", "initiative_ids_csv", "statuses_csv", "assignee_user_ids_csv", "team_ids_csv", "show_closed", "my_issues", "my_teams", "lens", "sort", "order"] as const;

function viewMemoryKey(accountId: string): string {
  return `atlas.issues.view:${accountId}`;
}

type SortColumn = "title" | "initiative" | "status" | "assignee" | "team" | "created_at";
type SortOrder = "asc" | "desc";
const PAGE_SIZE = 50;

interface ListResponse {
  issues: IssueListItem[];
  next_cursor: { createdAt: string; id: string } | null;
}

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
}

function parseCsvTyped<T extends string>(raw: string | null, allowed: readonly T[]): T[] {
  return parseCsv(raw).filter((s): s is T => (allowed as readonly string[]).includes(s));
}

export const EMPTY_ORG_TEXT = "No issues in your org.";
export const EMPTY_OPEN_TEXT = "No open issues. Check “Show closed” to see closed and dismissed issues.";
export const EMPTY_FILTERED_TEXT = "No issues match these filters.";

/** The table (spec §3.2), pure for the tests. `initiativeColumn` false = the initiative's own tab (spec §3.3), which shows Resolution instead. */
export function IssuesTable(props: {
  items: IssueListItem[];
  initiativeColumn?: boolean;
  sort?: SortColumn | null;
  order?: SortOrder;
  onToggleSort?: (col: SortColumn) => void;
  headers?: Partial<Record<"title" | "initiative" | "status" | "assignee" | "team", React.ReactNode>>;
  filterActive?: Partial<Record<"title" | "initiative" | "status" | "assignee" | "team", boolean>>;
}): JSX.Element {
  const withInitiative = props.initiativeColumn !== false;
  const h = props.headers ?? {};
  const f = props.filterActive ?? {};
  const sortProps = (sortKey: SortColumn) => (props.onToggleSort ? { sortKey, currentSort: props.sort ?? null, currentOrder: props.order, onToggleSort: props.onToggleSort } : {});
  return (
    <table className="table">
      <thead>
        <tr>
          <HeaderCell title="Title" {...sortProps("title")} isFilterActive={f.title}>
            {h.title}
          </HeaderCell>
          {withInitiative ? (
            <HeaderCell title="Initiative" {...sortProps("initiative")} isFilterActive={f.initiative}>
              {h.initiative}
            </HeaderCell>
          ) : null}
          <HeaderCell title="Status" {...sortProps("status")} isFilterActive={f.status}>
            {h.status}
          </HeaderCell>
          {!withInitiative ? <th>Resolution</th> : null}
          <HeaderCell title="Assigned to" {...sortProps("assignee")} isFilterActive={f.assignee}>
            {h.assignee}
          </HeaderCell>
          <HeaderCell title="Team" {...sortProps("team")} isFilterActive={f.team}>
            {h.team}
          </HeaderCell>
          <th>Raised</th>
          <HeaderCell title="Age" {...sortProps("created_at")} />
          <th>Action items</th>
        </tr>
      </thead>
      <tbody>
        {props.items.map((i) => (
          <tr key={i.id}>
            <td>
              <Link className="adminListName" to={`/app/issues/${i.id}`}>
                {i.title}
              </Link>
            </td>
            {withInitiative ? (
              <td>
                <Link className="adminListName" to={`/app/initiatives/${i.initiative.id}`}>
                  {initiativeLabel(i.initiative)}
                </Link>
              </td>
            ) : null}
            <td>
              <span className={statusPillClass(i.status)}>{STATUS_WORDS[i.status]}</span>
            </td>
            {!withInitiative ? <td className={i.resolution_label ? undefined : "muted"}>{i.resolution_label ?? "—"}</td> : null}
            <td>{i.assignee ? i.assignee.name : <span className="muted">Unassigned</span>}</td>
            <td className={i.team ? undefined : "muted"}>{i.team?.name ?? "—"}</td>
            <td title={i.raised_by ? `Raised by ${i.raised_by.name}` : undefined}>{fmtDay(i.created_at)}</td>
            <td className="muted" style={{ fontSize: 12 }}>
              {ageCell(i)}
            </td>
            <td className={i.action_items.total === 0 ? "muted" : undefined} style={{ fontVariantNumeric: "tabular-nums" }}>
              {actionItemsCell(i)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function TenantIssues(props: { entitlement?: string; callerAccountId: string }) {
  const canRaise = props.entitlement === "builder" || props.entitlement === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const titleContains = searchParams.get("title_contains") ?? "";
  const initiativeIds = parseCsv(searchParams.get("initiative_ids_csv"));
  const statuses = parseCsvTyped(searchParams.get("statuses_csv"), ISSUE_STATUSES);
  const assigneeIds = parseCsv(searchParams.get("assignee_user_ids_csv"));
  const teamIds = parseCsv(searchParams.get("team_ids_csv"));
  const lens: "org" | "all" = searchParams.get("lens") === "all" ? "all" : "org";
  const showClosed = searchParams.get("show_closed") === "1";
  const myIssues = searchParams.get("my_issues") === "1";
  const myTeams = searchParams.get("my_teams") === "1";
  const hideClosed = !showClosed && statuses.length === 0;
  const closedVisible = statuses.length > 0 ? statuses.some((s) => s === "closed" || s === "dismissed") : showClosed;

  const rawSort = searchParams.get("sort") as SortColumn | null;
  const sort: SortColumn = rawSort ?? "created_at";
  const order: SortOrder = searchParams.get("order") === "asc" ? "asc" : "desc";
  const isDefaultSort = sort === "created_at";

  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === "") p.delete(k);
            else p.set(k, v);
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // ---- View memory (session, per account) — the action-item list's wiring, verbatim.
  const memoryKey = viewMemoryKey(props.callerAccountId);
  const [memoryResolved, setMemoryResolved] = useState(false);
  const pendingRestoreRef = useRef<string | null>(null);

  useEffect(() => {
    const stored = readViewState(memoryKey);
    if (stored && !viewStateInUrl(searchParams, REMEMBERED_PARAMS)) {
      pendingRestoreRef.current = stored;
      setSearchParams((prev) => applyViewState(prev, stored, REMEMBERED_PARAMS), { replace: true });
    }
    setMemoryResolved(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey]);

  useEffect(() => {
    if (!memoryResolved) return;
    const serialized = serializeViewState(searchParams, REMEMBERED_PARAMS);
    if (pendingRestoreRef.current !== null) {
      if (serialized !== pendingRestoreRef.current) return;
      pendingRestoreRef.current = null;
    }
    writeViewState(memoryKey, serialized);
  }, [searchParams, memoryKey, memoryResolved]);

  // Reference data: the directory (users and groups) and the initiatives, for the filters' options.
  const [directory, setDirectory] = useState<MemberCandidate[]>([]);
  const [initiatives, setInitiatives] = useState<Array<{ id: string; ref?: string; name: string }>>([]);
  useEffect(() => {
    void (async () => {
      const [dir, inits] = await Promise.all([
        apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates").catch(() => ({ candidates: [] as MemberCandidate[] })),
        apiFetch<{ initiatives: Array<{ id: string; ref?: string; name: string }> }>("/api/tenant/initiatives?show_closed=1").catch(() => ({ initiatives: [] })),
      ]);
      setDirectory(dir.candidates);
      setInitiatives(inits.initiatives.map((i) => ({ id: i.id, ref: i.ref, name: i.name })));
    })();
  }, []);
  const users = useMemo(() => directory.filter((c) => c.kind === "user"), [directory]);
  const groups = useMemo(() => directory.filter((c) => c.kind === "group"), [directory]);
  const nameOf = (list: Array<{ id: string; name?: string; display_name?: string }>, id: string) => {
    const hit = list.find((x) => x.id === id);
    return hit ? (hit.name ?? hit.display_name ?? "—") : "—";
  };

  const [items, setItems] = useState<IssueListItem[]>([]);
  const [cursor, setCursor] = useState<{ createdAt: string; id: string } | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedOnceRef = useRef(false);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (titleContains.trim()) p.set("title_contains", titleContains.trim());
    if (initiativeIds.length) p.set("initiative_ids_csv", initiativeIds.join(","));
    if (statuses.length) p.set("statuses_csv", statuses.join(","));
    else if (!hideClosed) p.set("show_closed", "1");
    if (assigneeIds.length) p.set("assignee_user_ids_csv", assigneeIds.join(","));
    if (teamIds.length) p.set("team_ids_csv", teamIds.join(","));
    if (myIssues) p.set("my_issues", "1");
    if (myTeams) p.set("my_teams", "1");
    if (lens === "org") p.set("assignee_subtree_of", props.callerAccountId);
    if (rawSort) {
      p.set("sort", rawSort);
      p.set("order", order);
    }
    p.set("limit", String(PAGE_SIZE));
    return p.toString();
  }, [titleContains, initiativeIds, statuses, hideClosed, assigneeIds, teamIds, myIssues, myTeams, lens, props.callerAccountId, rawSort, order]);

  const loadFirstPage = useCallback(async () => {
    if (loadedOnceRef.current) setRefreshing(true);
    else setInitialLoading(true);
    setError(null);
    try {
      const res = await apiFetch<ListResponse>(`/api/tenant/issues?${queryString}`);
      setItems(res.issues);
      setCursor(res.next_cursor);
      loadedOnceRef.current = true;
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [queryString]);

  useEffect(() => {
    if (!memoryResolved) return;
    void loadFirstPage();
  }, [loadFirstPage, memoryResolved]);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const p = new URLSearchParams(queryString);
      p.set("cursor_created_at", cursor.createdAt);
      p.set("cursor_id", cursor.id);
      const res = await apiFetch<ListResponse>(`/api/tenant/issues?${p.toString()}`);
      setItems((prev) => [...prev, ...res.issues]);
      setCursor(res.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  function toggleSort(col: SortColumn) {
    if (sort !== col) setParams({ sort: col, order: "desc" });
    else setParams({ sort: col, order: order === "desc" ? "asc" : "desc" });
  }

  const filterActive = {
    title: titleContains.trim().length > 0,
    initiative: initiativeIds.length > 0,
    status: statuses.length > 0,
    assignee: assigneeIds.length > 0,
    team: teamIds.length > 0,
    myIssues,
    myTeams,
    showingClosed: showClosed,
  };
  const anyFilterActive = Object.values(filterActive).some((v) => v);

  function clearAllFilters() {
    setParams({ title_contains: null, initiative_ids_csv: null, statuses_csv: null, show_closed: null, assignee_user_ids_csv: null, team_ids_csv: null, my_issues: null, my_teams: null });
  }

  const [searchDraft, setSearchDraft] = useState(titleContains);
  useEffect(() => setSearchDraft(titleContains), [titleContains]);
  function applySearch() {
    const v = searchDraft.trim();
    if (v === titleContains.trim()) return;
    setParams({ title_contains: v || null });
  }
  function toggleShowClosed() {
    if (closedVisible) setParams({ show_closed: null, statuses_csv: null });
    else setParams({ show_closed: "1", statuses_csv: null });
  }

  return (
    <PageLayout
      title="Issues"
      actions={
        canRaise ? (
          <Link className="btn btnPrimary" to="/app/issues/new">
            + Raise issue
          </Link>
        ) : undefined
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div className="toolbar">
          <div className="toolbarGroup">
            <select
              className="select"
              value={lens}
              onChange={(e) => setParams({ lens: e.target.value === "all" ? "all" : null })}
              title="My org shows issues assigned to you, everyone in your org subtree, and contacts they proxy — and unassigned ones"
              aria-label="Assignee lens"
              style={{ width: "auto", fontSize: 13 }}
            >
              <option value="org">My org</option>
              <option value="all">Everyone</option>
            </select>
            <label title="Include closed and dismissed issues" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={closedVisible} onChange={toggleShowClosed} />
              Show closed
            </label>
            <label title="Only the issues assigned to you" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={myIssues} onChange={() => setParams({ my_issues: myIssues ? null : "1" })} />
              My issues
            </label>
            <label title="Only the issues whose team is one you belong to" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={myTeams} onChange={() => setParams({ my_teams: myTeams ? null : "1" })} />
              My teams
            </label>
            <input
              className="input"
              type="search"
              placeholder="Search titles…"
              title="Search issue titles — press Enter to apply"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onBlur={applySearch}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch();
              }}
              style={{ width: 340, fontSize: 13, flexShrink: 0 }}
            />
          </div>
        </div>

        {anyFilterActive && (
          <div className="filterSummaryStrip">
            <span>Filtered by:</span>
            {filterActive.title && <FilterChip label={`Title contains "${titleContains.trim()}"`} onClear={() => setParams({ title_contains: null })} />}
            {filterActive.initiative && <FilterChip label={`Initiative: ${initiativeIds.map((id) => nameOf(initiatives, id)).join(", ")}`} onClear={() => setParams({ initiative_ids_csv: null })} />}
            {filterActive.status && <FilterChip label={`Status: ${statuses.map((s) => STATUS_WORDS[s]).join(", ")}`} onClear={() => setParams({ statuses_csv: null })} />}
            {filterActive.assignee && <FilterChip label={`Assigned to: ${assigneeIds.map((id) => nameOf(users, id)).join(", ")}`} onClear={() => setParams({ assignee_user_ids_csv: null })} />}
            {filterActive.team && <FilterChip label={`Team: ${teamIds.map((id) => nameOf(groups, id)).join(", ")}`} onClear={() => setParams({ team_ids_csv: null })} />}
            {filterActive.myIssues && <FilterChip label="My issues" onClear={() => setParams({ my_issues: null })} />}
            {filterActive.myTeams && <FilterChip label="My teams" onClear={() => setParams({ my_teams: null })} />}
            {filterActive.showingClosed && <FilterChip label="Showing closed" onClear={() => setParams({ show_closed: null })} />}
            <button type="button" className="filterClearAll" onClick={clearAllFilters}>
              Clear all
            </button>
          </div>
        )}

        {error ? <ErrorState detail={error} /> : null}
        {initialLoading && !error && <LoadingState />}
        {!initialLoading && !error && items.length === 0 && !refreshing && (
          <div className="adminListEmpty">{lens === "org" && !anyFilterActive ? EMPTY_ORG_TEXT : hideClosed && !anyFilterActive ? EMPTY_OPEN_TEXT : EMPTY_FILTERED_TEXT}</div>
        )}
        {!initialLoading && !error && (items.length > 0 || refreshing) && (
          <div className="tableWrap" style={{ position: "relative" }}>
            {refreshing && (
              <div className="muted" style={{ position: "absolute", top: 4, right: 8, fontSize: 11, letterSpacing: 0.04, opacity: 0.8 }} aria-live="polite">
                Refreshing…
              </div>
            )}
            <IssuesTable
              items={items}
              sort={sort}
              order={order}
              onToggleSort={toggleSort}
              filterActive={filterActive}
              headers={{
                title: <TextFilterPopover value={titleContains} onApply={(v) => setParams({ title_contains: v || null })} />,
                initiative: (
                  <MultiSelectPopover
                    title="Filter by initiative"
                    options={initiatives.map((i) => ({ id: i.id, label: initiativeLabel(i) }))}
                    selected={initiativeIds}
                    onChange={(next) => setParams({ initiative_ids_csv: next.length ? next.join(",") : null })}
                    emptyMessage="No initiatives yet."
                  />
                ),
                status: (
                  <MultiSelectPopover
                    title="Filter by status"
                    options={ISSUE_STATUSES.map((s) => ({ id: s, label: STATUS_WORDS[s] }))}
                    selected={statuses}
                    onChange={(next) => setParams({ statuses_csv: next.length ? next.join(",") : null })}
                  />
                ),
                assignee: (
                  <MultiSelectPopover
                    title="Filter by who it's assigned to"
                    options={users.map((u) => ({ id: u.id, label: u.display_name }))}
                    selected={assigneeIds}
                    onChange={(next) => setParams({ assignee_user_ids_csv: next.length ? next.join(",") : null })}
                    emptyMessage="No users available."
                  />
                ),
                team: (
                  <MultiSelectPopover
                    title="Filter by team"
                    options={groups.map((g) => ({ id: g.id, label: g.display_name }))}
                    selected={teamIds}
                    onChange={(next) => setParams({ team_ids_csv: next.length ? next.join(",") : null })}
                    emptyMessage="No teams yet (Admin → Users & Teams)."
                  />
                ),
              }}
            />
          </div>
        )}

        {!initialLoading && !error && cursor && isDefaultSort && (
          <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
            <button className="btn" onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
        {!initialLoading && !error && items.length > 0 && !isDefaultSort && (
          <div className="muted" style={{ fontSize: 12, padding: "4px 0" }}>
            Showing up to {PAGE_SIZE} issues. Return to Age sort to page through older issues.
          </div>
        )}
      </div>
    </PageLayout>
  );
}
